import type { Continuity } from '@dissidium/contracts';
import { add } from '../kernel/primitives.ts';
import { assessLocalHearing, type HearingAssessment, type LocalEvidence, type WitnessProtection } from '../evidence/local.ts';
import type { MaterialAftermath } from '../economy/local.ts';
import {
  applyQuestNode,
  makeQuestState,
  resolvePersonalGoal,
  type PersonalGoal,
  type PersonalGoalOutcome,
  type QuestNode,
  type QuestState,
} from '../quests/graph.ts';
import { breakAnchor, releaseAnchor, tetherEvidenceId, TOTAL_KILN_BODIES, type RemainsOperation } from '../remains/index.ts';

export const SLICE_MILESTONES = ['slice.M0', 'slice.M1', 'slice.M2', 'slice.M3', 'slice.M4', 'slice.M5'] as const;
export const SLICE_OUTCOMES = ['public-dismantling', 'workers-shutdown', 'private-containment', 'licensed-atrocity', 'violent-destruction', 'departure-failure'] as const;

export type SliceMilestone = typeof SLICE_MILESTONES[number];
export type SliceOutcomeId = typeof SLICE_OUTCOMES[number];
export type SliceAction = QuestNode & Readonly<{
  milestone: SliceMilestone;
  category: 'inspection' | 'investigation' | 'evidence' | 'preparation' | 'support' | 'intimidation' | 'removal' | 'resolution' | 'accounting' | 'aftermath';
  pressureDelta: number;
  outcomeId?: SliceOutcomeId;
}>;
export type SliceOutcome = Readonly<{
  id: SliceOutcomeId;
  operationPolicy: 'release' | 'break' | 'leave-active';
  grainRequired: boolean;
  workerOutcome: string;
  practitionerOutcome: 'cleared-if-separated' | 'clear-if-evidence-preserved' | 'accused' | 'unresolved';
  marketAftermath: string;
  gateAftermath: string;
  allowedPersonalGoalStatuses: readonly PersonalGoalOutcome['status'][];
}>;
export type SliceArc = Readonly<{
  id: string;
  caseId: string;
  initialFacts: readonly string[];
  milestones: readonly { id: SliceMilestone; label: string }[];
  removalMilestoneId: string;
  actions: readonly SliceAction[];
  outcomes: readonly SliceOutcome[];
}>;

export type SliceReport = Readonly<{
  outcomeId: SliceOutcomeId;
  completedMilestones: readonly SliceMilestone[];
  necromancyPressure: number;
  removal: 'not-attempted' | 'attempted' | 'intercepted';
  grain: Readonly<{ status: 'resolved'; aftermath: MaterialAftermath }> | Readonly<{ status: 'left-unresolved'; aftermath: null }>;
  workers: string;
  practitionerAccusation: 'cleared' | 'accused' | 'unresolved';
  remains: readonly Readonly<{ id: string; disposition: 'exposed' | 'buried' | 'animated' | 'destroyed' }>[];
  personalGoal: Readonly<{ status: PersonalGoalOutcome['status']; summary: string }>;
  hearing: HearingAssessment;
  evidencePreserved: boolean;
  marketAftermath: string;
  gateAftermath: string;
}>;

export type SliceState = Readonly<{
  quest: QuestState;
  continuity: Continuity;
  operation: RemainsOperation;
  necromancyPressure: number;
  outcomeId: SliceOutcomeId | null;
  report: SliceReport | null;
}>;

function assertArc(arc: SliceArc): void {
  if (arc.milestones.map(({ id }) => id).join('|') !== SLICE_MILESTONES.join('|')) throw new Error('The slice arc must contain slice.M0 through slice.M5 in order');
  if (arc.outcomes.map(({ id }) => id).join('|') !== SLICE_OUTCOMES.join('|')) throw new Error('The slice arc must contain all six outcomes in order');
  if (!arc.actions.some(({ id }) => id === arc.removalMilestoneId)) throw new Error('The slice arc is missing its named removal milestone');
  for (const action of arc.actions) {
    const feedsNecromancy = action.category === 'support' || action.category === 'intimidation';
    if (feedsNecromancy ? action.pressureDelta <= 0 : action.pressureDelta !== 0) throw new Error(`${action.id} violates the support-only necromancy pressure rule`);
  }
}

function remainsIds(operation: RemainsOperation): string[] {
  return [...operation.stations.flatMap(station => station.bodies.map(body => body.remainsId)), ...operation.storage.map(body => body.remainsId)].sort();
}

export function createSliceState(arc: SliceArc, continuity: Continuity, operation: RemainsOperation): SliceState {
  assertArc(arc);
  const ids = remainsIds(operation);
  if (ids.length !== TOTAL_KILN_BODIES || new Set(ids).size !== TOTAL_KILN_BODIES) throw new Error('The slice must begin with exactly nine distinct remains');
  return { quest: makeQuestState(arc.initialFacts), continuity: structuredClone(continuity), operation: structuredClone(operation), necromancyPressure: 0, outcomeId: null, report: null };
}

export function performSliceAction(arc: SliceArc, state: SliceState, actionId: string): SliceState {
  assertArc(arc);
  if (state.report) throw new Error('The resolved slice cannot accept more actions');
  const action = arc.actions.find(candidate => candidate.id === actionId);
  if (!action) throw new Error(`Unknown slice action ${actionId}`);
  if (action.milestone === 'slice.M4' || action.milestone === 'slice.M5') throw new Error(`${action.id} is applied by terminal accounting`);
  const quest = applyQuestNode(arc.actions, state.quest, actionId);
  return {
    ...state,
    quest,
    necromancyPressure: add(state.necromancyPressure, action.pressureDelta),
    outcomeId: action.outcomeId ?? state.outcomeId,
  };
}

function settleOperation(
  continuity: Continuity,
  operation: RemainsOperation,
  outcome: SliceOutcome,
  combatCare: 'careful' | 'reckless' | null,
): { continuity: Continuity; operation: RemainsOperation } {
  let result = { continuity, operation };
  for (const station of operation.stations) {
    if (station.integrity !== 'intact' || outcome.operationPolicy === 'leave-active') continue;
    result = outcome.operationPolicy === 'release'
      ? releaseAnchor(result.continuity, result.operation, station.id, [tetherEvidenceId(station.id)])
      : breakAnchor(result.continuity, result.operation, station.id, combatCare === 'careful' ? 'force' : 'fire');
  }
  return result;
}

function practitionerResult(outcome: SliceOutcome, quest: QuestState, hearing: HearingAssessment, evidencePreserved: boolean): SliceReport['practitionerAccusation'] {
  if (outcome.practitionerOutcome === 'accused' || outcome.practitionerOutcome === 'unresolved') return outcome.practitionerOutcome;
  if (outcome.practitionerOutcome === 'cleared-if-separated') return quest.facts.includes('fact:melian-separated') && hearing.credible ? 'cleared' : 'accused';
  return evidencePreserved && hearing.credible && quest.facts.includes('fact:melian-separated') ? 'cleared' : 'accused';
}

export function resolveSlice(
  arc: SliceArc,
  state: SliceState,
  input: Readonly<{
    grain: MaterialAftermath | null;
    evidence: readonly LocalEvidence[];
    protections: readonly WitnessProtection[];
    personalGoal: PersonalGoal;
    personalGoalOutcome: PersonalGoalOutcome;
    combatCare?: 'careful' | 'reckless';
    /** Interactive hosts record M5 only after the player revisits the changed places. */
    deferAftermath?: boolean;
  }>,
): SliceState {
  assertArc(arc);
  if (state.report) throw new Error('The slice is already resolved');
  const outcome = arc.outcomes.find(candidate => candidate.id === state.outcomeId);
  if (!outcome) throw new Error('Choose a slice.M3 outcome before terminal accounting');
  // A life that dies before settling the grain leaves it unresolved.
  const died = state.continuity.persons[input.personalGoal.personId]?.lifecycle === 'dead';
  if (outcome.grainRequired && !input.grain && !died) throw new Error(`${outcome.id} requires one of the four lift remedies`);
  if (!outcome.allowedPersonalGoalStatuses.includes(input.personalGoalOutcome.status)) throw new Error(`${input.personalGoalOutcome.status} is not allowed for ${outcome.id}`);
  if (outcome.operationPolicy === 'break' && !input.combatCare) throw new Error('Violent destruction must record careful or reckless combat');

  const hearing = assessLocalHearing(state.continuity, arc.caseId, input.evidence, input.protections);
  if (outcome.id === 'public-dismantling' && !hearing.credible) throw new Error('Public dismantling requires two independent evidence types');
  const settledOperation = settleOperation(state.continuity, state.operation, outcome, input.combatCare ?? null);
  const goal = resolvePersonalGoal(settledOperation.continuity, state.quest, input.personalGoal, input.personalGoalOutcome);
  let quest = applyQuestNode(arc.actions, goal.quest, 'slice.M4-account-grain-and-burial');
  if (!input.deferAftermath) quest = applyQuestNode(arc.actions, quest, 'slice.M5-observe-local-aftermath');

  const remains = remainsIds(settledOperation.operation).map(id => {
    const disposition = goal.continuity.remains[id]?.disposition;
    if (!disposition) throw new Error(`${id} is missing from continuity at terminal accounting`);
    return { id, disposition };
  });
  if (remains.length !== TOTAL_KILN_BODIES) throw new Error('Terminal accounting requires exactly nine remains');
  const evidencePreserved = remains.every(record => record.disposition !== 'destroyed');
  const completedMilestones = SLICE_MILESTONES.filter(milestone => arc.actions.some(action => action.milestone === milestone && quest.completedNodeIds.includes(action.id)));
  const removal = quest.facts.includes('fact:removal-intercepted') ? 'intercepted' : quest.facts.includes('fact:removal-attempted') ? 'attempted' : 'not-attempted';
  const grain = input.grain ? { status: 'resolved' as const, aftermath: structuredClone(input.grain) } : { status: 'left-unresolved' as const, aftermath: null };
  const report: SliceReport = {
    outcomeId: outcome.id,
    completedMilestones,
    necromancyPressure: state.necromancyPressure,
    removal,
    grain,
    workers: outcome.workerOutcome,
    practitionerAccusation: practitionerResult(outcome, quest, hearing, evidencePreserved),
    remains,
    personalGoal: { status: input.personalGoalOutcome.status, summary: input.personalGoalOutcome.summary },
    hearing,
    evidencePreserved,
    marketAftermath: outcome.marketAftermath,
    gateAftermath: outcome.gateAftermath,
  };
  return { ...state, quest, continuity: goal.continuity, operation: settledOperation.operation, report };
}
